import configuration from "react-global-configuration";

/**
 * Appearance Engine — CSS custom properties applied from settings.
 * Single source of truth for platform theming (White Label ready).
 * Theme changes do not require a frontend rebuild.
 */

export const FALLBACK_CSS_VARIABLES = {
  /* ——— Brand / foundations ——— */
  "--color-primary": "#e50914",
  "--color-secondary": "#6c757d",
  "--color-accent": "#e50914",
  "--color-accent-secondary": "#f40612",
  "--color-success": "#28a745",
  "--color-warning": "#ffc107",
  "--color-danger": "#dc3545",
  "--color-error": "#dc3545",
  "--color-info": "#17a2b8",
  "--color-neutral": "#6c757d",
  "--color-link": "#70b7fd",
  "--color-link-hover": "#9dceff",
  "--color-link-visited": "#b39ddb",
  "--theme-mode": "dark",

  "--radius": "0",
  "--radius-sm": "0.25rem",
  "--radius-md": "0.5rem",
  "--radius-lg": "0.75rem",
  "--shadow-button": "2px 4px 10px rgba(0,0,0,.1)",
  "--shadow-elevation-1": "0 1px 3px rgba(0,0,0,.2)",
  "--shadow-elevation-2": "0 4px 12px rgba(0,0,0,.28)",
  "--transition-fast": "150ms ease",
  "--transition-base": "200ms ease",
  "--space-1": "0.25rem",
  "--space-2": "0.5rem",
  "--space-3": "0.75rem",
  "--space-4": "1rem",
  "--space-5": "1.5rem",
  "--space-6": "2rem",

  /* ——— Chrome ——— */
  "--header-bg": "rgba(0,0,0,.97)",
  "--header-text": "#cccccc",
  "--header-hover": "#ffffff",
  "--header-active": "#ffffff",
  "--header-icon": "#cccccc",
  "--header-icon-hover": "#ffffff",
  "--footer-bg": "#000000",
  "--footer-text": "#999999",
  "--footer-link": "#999999",
  "--footer-hover": "#ffffff",
  "--footer-padding": "2em",
  "--body-bg": "#222222",
  "--body-text": "#ffffff",

  /* ——— Buttons ——— */
  "--button-primary": "#e50914",
  "--button-primary-hover": "#f40612",
  "--button-primary-text": "#ffffff",
  "--button-secondary": "#3d3d3d",
  "--button-secondary-hover": "#4a4a4a",
  "--button-secondary-text": "#ffffff",
  "--button-accent": "#f40612",
  "--button-accent-hover": "#ff1a26",
  "--button-accent-text": "#ffffff",
  "--button-accent-border": "#f40612",
  "--button-outline-bg": "transparent",
  "--button-outline-border": "#e50914",
  "--button-outline-text": "#e50914",
  "--button-outline-hover-bg": "#e50914",
  "--button-outline-hover-text": "#ffffff",
  "--button-ghost-bg": "transparent",
  "--button-ghost-text": "#ffffff",
  "--button-ghost-hover-bg": "rgba(255,255,255,.08)",
  "--button-text-color": "var(--color-accent, #e50914)",
  "--button-text-hover": "var(--color-accent-secondary, #f40612)",
  "--button-destructive": "#dc3545",
  "--button-destructive-hover": "#c82333",
  "--button-destructive-text": "#ffffff",
  "--button-border": "#e50914",
  "--button-radius": "0",
  "--button-shadow": "2px 4px 10px rgba(0,0,0,.1)",
  "--button-variant": "filled",
  "--button-disabled-opacity": "0.55",
  "--button-gradient": "none",
  "--button-font-weight": "600",
  "--button-padding-y": "0.55rem",
  "--button-padding-x": "1.15rem",

  /* Entity action semantics (Organization reference implementation) */
  "--entity-action-donate-bg": "var(--button-primary, #e50914)",
  "--entity-action-donate-hover": "var(--button-primary-hover, #f40612)",
  "--entity-action-donate-text": "var(--button-primary-text, #ffffff)",
  "--entity-action-donate-border": "var(--button-border, #e50914)",
  "--entity-action-follow-bg": "var(--button-secondary, #3d3d3d)",
  "--entity-action-follow-hover": "var(--button-secondary-hover, #4a4a4a)",
  "--entity-action-follow-text": "var(--button-secondary-text, #ffffff)",
  "--entity-action-follow-border": "var(--button-secondary, #3d3d3d)",
  "--entity-action-following-bg": "var(--button-secondary, #3d3d3d)",
  "--entity-action-following-text": "var(--button-secondary-text, #ffffff)",
  "--entity-action-following-border": "var(--button-secondary, #3d3d3d)",
  "--entity-action-website-bg": "var(--button-accent, #f40612)",
  "--entity-action-website-hover": "var(--button-accent-hover, #ff1a26)",
  "--entity-action-website-text": "var(--button-accent-text, #ffffff)",
  "--entity-action-website-border": "var(--button-accent-border, #f40612)",
  "--entity-action-share-bg": "transparent",
  "--entity-action-share-hover-bg": "rgba(255,255,255,.08)",
  "--entity-action-share-text": "var(--button-ghost-text, #ffffff)",
  "--entity-action-share-border": "transparent",
  "--entity-action-text-color": "var(--button-text-color, #e50914)",
  "--entity-action-text-hover": "var(--button-text-hover, #f40612)",
  "--entity-action-donate-icon": "var(--entity-action-donate-text, #ffffff)",
  "--entity-action-follow-icon": "var(--entity-action-follow-text, #ffffff)",
  "--entity-action-following-icon": "var(--entity-action-following-text, #ffffff)",
  "--entity-action-following-hover": "var(--button-secondary-hover, #4a4a4a)",
  "--entity-action-website-icon": "var(--entity-action-website-text, #ffffff)",
  "--entity-action-share-icon": "var(--entity-action-share-text, #ffffff)",
  "--entity-action-radius": "var(--button-radius, 0)",
  "--entity-action-shadow": "var(--button-shadow, none)",
  "--entity-action-transition": "var(--transition-fast, 150ms ease)",
  "--entity-action-disabled-opacity": "var(--button-disabled-opacity, 0.55)",

  /* Entity media — Product-approved Organization reference sizes (Sprint 2.6) */
  "--entity-profile-size": "160px",
  "--entity-profile-size-tablet": "140px",
  "--entity-profile-size-mobile": "112px",
  "--entity-cover-min-height": "450px",
  "--entity-cover-min-height-tablet": "360px",
  "--entity-cover-min-height-mobile": "260px",
  "--entity-header-spacing": "1.5rem",
  "--social-icon-bg": "#4a4a4a",
  "--social-icon-size": "2em",
  "--social-icon-gap": "0.65rem",
  "--social-icon-radius": "50%",
  "--social-icon-border": "transparent",
  "--social-icon-hover-opacity": "0.85",
  "--social-icon-glyph": "#ffffff",
  "--social-icon-color-mode": "network",
  "--social-icon-shape": "circle",

  /* ——— Cards ——— */
  "--card-bg": "#1a1a1a",
  "--card-border": "#3d3d3d",
  "--card-radius": "0",
  "--card-shadow": "none",
  "--card-padding": "1rem",

  /* Video card downward expand/open arrow (matches arrow-red.png) */
  "--video-expand-arrow-color": "#e50a14",

  /* ——— Typography ——— */
  "--font-family": "'Regular', sans-serif",
  "--font-heading": "'Regular', sans-serif",
  "--font-body": "'Regular', sans-serif",
  "--heading-color": "#ffffff",
  "--type-page-title-size": "clamp(1.65rem, 3.5vw, 2.15rem)",
  "--type-page-title-weight": "700",
  "--type-section-title-size": "1.25rem",
  "--type-section-title-weight": "700",
  "--type-body-size": "1rem",
  "--type-body-weight": "400",
  "--type-caption-size": "0.875rem",
  "--type-caption-weight": "400",
  "--type-metadata-size": "0.8rem",
  "--type-metadata-weight": "400",
  "--type-metadata-color": "rgba(255,255,255,.55)",

  /* ——— Handles / usernames ——— */
  "--handle-color": "rgba(255,255,255,.72)",
  "--handle-hover": "#ffffff",
  "--handle-visited": "rgba(255,255,255,.55)",
  "--handle-weight": "500",
  "--handle-size": "0.98rem",
  "--handle-letter-spacing": "0.01em",

  /* ——— Entity name (readable on dark body by default) ——— */
  "--org-name-color": "#ffffff",
  "--org-type-color": "rgba(255,255,255,.62)",

  /* ——— Badges ——— */
  "--badge-verified-bg": "#e50914",
  "--badge-verified-text": "#ffffff",
  "--badge-featured-bg": "#17a2b8",
  "--badge-featured-text": "#ffffff",
  "--badge-partner-bg": "#6f42c1",
  "--badge-partner-text": "#ffffff",
  "--badge-official-bg": "#343a40",
  "--badge-official-text": "#ffffff",
  "--badge-mission-bg": "#20c997",
  "--badge-mission-text": "#ffffff",
  "--badge-creator-bg": "#fd7e14",
  "--badge-creator-text": "#ffffff",
  "--badge-organization-bg": "#0d6efd",
  "--badge-organization-text": "#ffffff",
  "--badge-project-bg": "#198754",
  "--badge-project-text": "#ffffff",
  "--badge-radius": "0.25rem",
  "--badge-font-size": "0.75rem",
  "--badge-font-weight": "600",
  "--badge-padding-y": "0.28em",
  "--badge-padding-x": "0.55em",
  "--badge-gap": "0.35rem",

  /* ——— Forms ——— */
  "--form-input-bg": "#1a1a1a",
  "--form-input-border": "#3d3d3d",
  "--form-input-text": "#ffffff",
  "--form-input-placeholder": "rgba(255,255,255,.45)",
  "--form-input-radius": "0",
  "--form-label-color": "rgba(255,255,255,.85)",
  "--form-focus-ring": "var(--button-primary, #e50914)",
  "--form-error-color": "var(--color-error, #dc3545)",

  /* ——— Profile image ——— */
  "--profile-image-border-style": "solid",
  "--profile-image-border-width": "1px",
  "--profile-image-border-color": "#C8C8C8",
  "--profile-image-radius": "6px",
  "--profile-image-shadow":
    "0 1px 2px rgba(0,0,0,.06), 0 4px 12px rgba(0,0,0,.06)",
  "--profile-image-padding": "2px",

  /* ——— Community ——— */
  "--community-page-bg": "#222222",
  "--community-feed-text": "#ffffff",
  "--community-card-bg": "#1a1a1a",
  "--community-card-border": "#3d3d3d",
  "--community-card-radius": "0",
  "--community-nav-link": "#cccccc",
  "--community-nav-active": "#ffffff",
  "--community-composer-bg": "rgba(0,0,0,.22)",
  "--community-empty-text": "#bbbbbb",
  "--community-sidebar-bg": "transparent",
  "--community-ad-slot-bg": "rgba(255,255,255,.04)",
  "--community-prayer-request-border": "#c9a227",
};

export function getAppearanceConfig() {
  const config = configuration.get("configData.appearance");
  if (config && typeof config === "object") {
    return config;
  }
  return {
    version: 1,
    schema_version: 1,
    active_profile_id: null,
    profile: null,
    css_variables: FALLBACK_CSS_VARIABLES,
  };
}

export function applyAppearanceTheme(appearanceConfig) {
  if (typeof document === "undefined") {
    return;
  }

  const root = document.documentElement;
  const incoming =
    appearanceConfig &&
    appearanceConfig.css_variables &&
    typeof appearanceConfig.css_variables === "object"
      ? appearanceConfig.css_variables
      : {};

  const merged = { ...FALLBACK_CSS_VARIABLES, ...incoming };

  const primary = merged["--button-primary"] || "#e50914";
  const hover = merged["--button-primary-hover"] || "#f40612";
  const gradientRaw = merged["--button-gradient"];
  if (!gradientRaw || gradientRaw === "none") {
    merged["--button-gradient"] = `linear-gradient(to bottom, ${primary}, ${hover})`;
  }

  // Keep brand aliases in sync when only legacy accent/button tokens are set.
  if (!incoming["--color-primary"] && merged["--button-primary"]) {
    merged["--color-primary"] = merged["--button-primary"];
  }
  if (!incoming["--color-danger"] && merged["--color-error"]) {
    merged["--color-danger"] = merged["--color-error"];
  }
  if (!incoming["--button-outline-border"]) {
    merged["--button-outline-border"] = merged["--button-primary"];
  }
  if (!incoming["--button-outline-text"]) {
    merged["--button-outline-text"] = merged["--button-primary"];
  }
  if (!incoming["--button-outline-hover-bg"]) {
    merged["--button-outline-hover-bg"] = merged["--button-primary"];
  }
  if (!incoming["--button-text-color"]) {
    merged["--button-text-color"] = merged["--color-accent"] || primary;
  }

  [
    "--button-radius",
    "--radius",
    "--radius-sm",
    "--radius-md",
    "--radius-lg",
    "--card-radius",
    "--badge-radius",
    "--form-input-radius",
    "--profile-image-border-width",
    "--profile-image-radius",
    "--profile-image-padding",
    "--entity-profile-size",
    "--entity-profile-size-tablet",
    "--entity-profile-size-mobile",
    "--entity-cover-min-height",
    "--entity-cover-min-height-tablet",
    "--entity-cover-min-height-mobile",
    "--entity-header-spacing",
    "--social-icon-size",
    "--social-icon-gap",
    "--social-icon-radius",
    "--entity-action-radius",
  ].forEach((key) => {
    if (merged[key] !== undefined && merged[key] !== null) {
      merged[key] = normalizeCssLength(merged[key]);
    }
  });

  const profileBorderStyle = String(
    merged["--profile-image-border-style"] || "solid"
  )
    .trim()
    .toLowerCase();
  merged["--profile-image-border-style"] =
    profileBorderStyle === "solid" ? "solid" : "none";

  const profileRadiusKey = String(merged["--profile-image-radius"] || "6px")
    .trim()
    .toLowerCase();
  const profileRadiusMap = {
    square: "0",
    slightly_rounded: "6px",
    rounded: "12px",
    "0px": "0",
  };
  if (profileRadiusMap[profileRadiusKey] !== undefined) {
    merged["--profile-image-radius"] = profileRadiusMap[profileRadiusKey];
  }

  const profileShadowKey = String(merged["--profile-image-shadow"] || "soft")
    .trim()
    .toLowerCase();
  const profileShadowMap = {
    none: "none",
    soft: "0 1px 2px rgba(0,0,0,.06), 0 4px 12px rgba(0,0,0,.06)",
    medium: "0 2px 6px rgba(0,0,0,.10), 0 8px 20px rgba(0,0,0,.12)",
  };
  if (profileShadowMap[profileShadowKey] !== undefined) {
    merged["--profile-image-shadow"] = profileShadowMap[profileShadowKey];
  }

  if (merged["--profile-image-border-style"] === "none") {
    merged["--profile-image-border-width"] = "0px";
    merged["--profile-image-padding"] = "0px";
  } else if (!merged["--profile-image-padding"]) {
    merged["--profile-image-padding"] = "2px";
  }

  // Light-mode handle defaults when theme mode is light and handles not overridden.
  const mode = String(merged["--theme-mode"] || "dark")
    .trim()
    .toLowerCase();
  if (mode === "light") {
    if (!incoming["--handle-color"]) {
      merged["--handle-color"] = "rgba(0,0,0,.55)";
    }
    if (!incoming["--handle-hover"]) {
      merged["--handle-hover"] = "rgba(0,0,0,.85)";
    }
    if (!incoming["--handle-visited"]) {
      merged["--handle-visited"] = "rgba(0,0,0,.45)";
    }
    if (!incoming["--org-name-color"]) {
      merged["--org-name-color"] = "#111111";
    }
    if (!incoming["--org-type-color"]) {
      merged["--org-type-color"] = "#707070";
    }
    if (!incoming["--button-ghost-text"]) {
      merged["--button-ghost-text"] = "#111111";
    }
    if (!incoming["--button-ghost-hover-bg"]) {
      merged["--button-ghost-hover-bg"] = "rgba(0,0,0,.06)";
    }
  } else {
    // Dark surfaces: neutralize legacy near-black entity name tokens from older presets.
    const nameColor = String(merged["--org-name-color"] || "")
      .trim()
      .toLowerCase();
    if (
      !incoming["--org-name-color"] ||
      nameColor === "#111111" ||
      nameColor === "#111" ||
      nameColor === "#000" ||
      nameColor === "#000000" ||
      nameColor === "black"
    ) {
      merged["--org-name-color"] = "#ffffff";
    }
    const typeColor = String(merged["--org-type-color"] || "")
      .trim()
      .toLowerCase();
    if (
      !incoming["--org-type-color"] ||
      typeColor === "#b0b0b0" ||
      typeColor === "#707070"
    ) {
      // Keep muted type color readable on dark; only force when missing.
      if (!incoming["--org-type-color"]) {
        merged["--org-type-color"] = "rgba(255,255,255,.62)";
      }
    }
  }

  // Entity actions inherit Appearance button radius (do not force rectangles).
  const entityRadius = String(merged["--entity-action-radius"] || "").trim();
  if (
    !entityRadius ||
    entityRadius === "0" ||
    entityRadius === "0px" ||
    entityRadius.toLowerCase() === "none"
  ) {
    merged["--entity-action-radius"] = "var(--button-radius)";
  }

  // Restore Product-approved entity media sizes (Sprint 2.6 visual references).
  const profileSize = String(merged["--entity-profile-size"] || "").trim();
  if (
    profileSize === "96px" ||
    profileSize === "96" ||
    profileSize === "128px" ||
    profileSize === "128"
  ) {
    merged["--entity-profile-size"] = "160px";
  }
  const profileTablet = String(merged["--entity-profile-size-tablet"] || "").trim();
  if (
    profileTablet === "84px" ||
    profileTablet === "84" ||
    profileTablet === "108px" ||
    profileTablet === "108"
  ) {
    merged["--entity-profile-size-tablet"] = "140px";
  }
  const profileMobile = String(merged["--entity-profile-size-mobile"] || "").trim();
  if (
    profileMobile === "72px" ||
    profileMobile === "72" ||
    profileMobile === "88px" ||
    profileMobile === "88"
  ) {
    merged["--entity-profile-size-mobile"] = "112px";
  }
  const coverHeight = String(merged["--entity-cover-min-height"] || "").trim();
  if (
    coverHeight === "280px" ||
    coverHeight === "280" ||
    coverHeight === "340px" ||
    coverHeight === "340"
  ) {
    merged["--entity-cover-min-height"] = "450px";
  }
  const coverTablet = String(merged["--entity-cover-min-height-tablet"] || "").trim();
  if (
    coverTablet === "240px" ||
    coverTablet === "240" ||
    coverTablet === "280px" ||
    coverTablet === "280"
  ) {
    merged["--entity-cover-min-height-tablet"] = "360px";
  }
  const coverMobile = String(merged["--entity-cover-min-height-mobile"] || "").trim();
  if (
    coverMobile === "200px" ||
    coverMobile === "200" ||
    coverMobile === "220px" ||
    coverMobile === "220"
  ) {
    merged["--entity-cover-min-height-mobile"] = "260px";
  }

  Object.keys(merged).forEach((key) => {
    if (!key || key.indexOf("--") !== 0) {
      return;
    }
    const value = merged[key];
    if (value === undefined || value === null || value === "") {
      return;
    }
    root.style.setProperty(key, String(value));
  });

  const variant = String(merged["--button-variant"] || "filled")
    .trim()
    .toLowerCase();
  root.setAttribute("data-button-variant", variant || "filled");
  root.setAttribute("data-theme-mode", mode === "light" ? "light" : "dark");

  const socialMode = String(merged["--social-icon-color-mode"] || "network")
    .trim()
    .toLowerCase();
  root.setAttribute(
    "data-social-icon-color-mode",
    socialMode === "mono" || socialMode === "custom" ? socialMode : "network"
  );
  const socialShape = String(merged["--social-icon-shape"] || "circle")
    .trim()
    .toLowerCase();
  root.setAttribute(
    "data-social-icon-shape",
    socialShape === "square" ? "square" : "circle"
  );

  let styleEl = document.getElementById("appearance-custom");
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = "appearance-custom";
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = "";
}

export function applyAppearanceFromConfiguration() {
  applyAppearanceTheme(getAppearanceConfig());
}

/**
 * Ensure length tokens are valid CSS (unitless numbers become px).
 */
export function normalizeCssLength(value) {
  const raw = String(value == null ? "" : value).trim();
  if (raw === "" || raw.toLowerCase() === "none" || raw.toLowerCase() === "auto") {
    return raw === "" ? "0" : raw;
  }
  if (raw === "0") {
    return "0";
  }
  if (
    /^calc\(/i.test(raw) ||
    /^var\(/i.test(raw) ||
    /^-?\d+(\.\d+)?(px|em|rem|%|vh|vw|vmin|vmax|ch|ex)$/i.test(raw)
  ) {
    return raw;
  }
  if (/^-?\d+(\.\d+)?$/.test(raw)) {
    return `${raw}px`;
  }
  return raw;
}
